import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Star, MessageSquare, Trash2, Loader2, Heart, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui';
import { useTranslation } from '@parentpath/i18n';
import { useMatches } from '../hooks/useMatches';
import type { NameMatch } from '../types';

// Gender icon components
const GenderIcon = ({ gender }: { gender: 'male' | 'female' | 'neutral' }) => {
  const symbols = {
    male: { symbol: '♂', className: 'gender-male' },
    female: { symbol: '♀', className: 'gender-female' },
    neutral: { symbol: '⚥', className: 'gender-neutral' },
  };
  const config = symbols[gender];
  return <span className={cn('text-lg', config.className)}>{config.symbol}</span>;
};

export function MatchListPage() {
  const { t } = useTranslation('names');
  const {
    matches,
    isLoading,
    filter,
    setFilter,
    totalMatches,
    shortlistedCount,
    toggleShortlist,
    updateNotes,
    removeMatch,
  } = useMatches();

  const [editingMatch, setEditingMatch] = useState<NameMatch | null>(null);
  const [noteText, setNoteText] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleStartEditNote = (match: NameMatch) => {
    setEditingMatch(match);
    setNoteText(match.notes || '');
  };

  const handleSaveNote = () => {
    if (editingMatch) {
      updateNotes(editingMatch.id, noteText);
      setEditingMatch(null);
      setNoteText('');
    }
  };

  const handleCloseNoteModal = () => {
    setEditingMatch(null);
    setNoteText('');
  };

  const handleDelete = (matchId: string) => {
    removeMatch(matchId);
    setConfirmDeleteId(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <h1 className="text-white text-xl font-semibold mb-4">{t('matchesPage.title')}</h1>

      {/* Filter Tabs */}
      <div className="flex items-center gap-3 mb-4 mt-8">
        <button
          onClick={() => setFilter('all')}
          className={cn(
            'filter-tab',
            filter === 'all' && 'active'
          )}
        >
          {t('matchesPage.all')}
          <span className="ml-1">{totalMatches}</span>
        </button>
        <button
          onClick={() => setFilter('shortlisted')}
          className={cn(
            'filter-tab',
            filter === 'shortlisted' && 'active'
          )}
        >
          {t('matchesPage.shortlist')}
          <span className="ml-1">{shortlistedCount}</span>
        </button>
      </div>

      {/* Matches List */}
      <section>
        {matches.length === 0 ? (
          <div className="glass-effect rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-white/60" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">
              {filter === 'shortlisted' ? t('matchesPage.emptyShortlist') : t('matchesPage.empty')}
            </h3>
            <p className="text-white/70 mb-4">
              {filter === 'shortlisted'
                ? t('matchesPage.emptyShortlistHint')
                : t('matchesPage.emptyHint')}
            </p>
            {filter === 'all' && (
              <Link to="/tools/names">
                <Button className="bg-pp-orange hover:bg-pp-orange/90">
                  {t('matchesPage.swipeNames')}
                </Button>
              </Link>
            )}
          </div>
        ) : (
          matches.map((match) => {
            const name = match.baby_name;
            if (!name) return null;

            return (
              <div key={match.id}>
                <div className="name-list-card">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-white font-semibold text-lg">{name.name}</span>
                      <GenderIcon gender={name.gender} />
                    </div>
                    {name.meaning && (
                      <div className="text-white italic text-sm mb-1">{name.meaning}</div>
                    )}
                    <div className="text-white/50 text-xs">
                      {t('matchesPage.matchDate', { date: new Date(match.matched_at).toLocaleDateString('de-DE') })}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-4">
                    {confirmDeleteId === match.id ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDelete(match.id)}
                          className="text-red-400 text-sm font-medium whitespace-nowrap"
                        >
                          {name.name} {t('matchesPage.delete')}
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          className="list-action-btn"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => toggleShortlist(match.id, match.is_shortlisted)}
                          className={cn(
                            'list-action-btn favorite',
                            match.is_shortlisted && 'text-pp-orange'
                          )}
                        >
                          <Star className={cn('w-5 h-5', match.is_shortlisted && 'fill-current')} />
                        </button>
                        <button
                          onClick={() => handleStartEditNote(match)}
                          className="list-action-btn"
                        >
                          <MessageSquare className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(match.id)}
                          className="list-action-btn delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Notes display - below card row */}
                {match.notes && (
                  <div
                    className="pb-3 -mt-1 cursor-pointer"
                    onClick={() => handleStartEditNote(match)}
                  >
                    <div className="bg-white/10 p-2 rounded">
                      {match.notes_author?.first_name && (
                        <span className="text-xs text-pp-orange font-medium">
                          {match.notes_author.first_name}:
                        </span>
                      )}
                      <p className="text-sm text-white/80">
                        {match.notes}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </section>

      {/* Comment Edit Modal - Bottom Sheet (portal to escape overflow stacking context) */}
      {editingMatch && editingMatch.baby_name && createPortal(
        <>
          <div
            className="fixed inset-0 bg-pp-dark/80 backdrop-blur-sm z-[9999]"
            onClick={handleCloseNoteModal}
          />
          <div className="fixed inset-x-0 bottom-0 z-[9999] flex items-end sm:items-center justify-center sm:inset-0">
            <div className="bg-pp-dark/35 backdrop-blur-md border-t border-white/25 rounded-t-2xl sm:rounded-[15px] w-full sm:max-w-md sm:mx-4 max-h-[85vh] overflow-hidden animate-in slide-in-from-bottom sm:zoom-in-95 duration-300 pb-6">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <h2 className="text-base font-semibold text-white">{t('matchesPage.editComment')}</h2>
                <button
                  onClick={handleCloseNoteModal}
                  className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Name Info */}
              <div className="p-4 border-b border-white/10">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-white font-semibold text-lg">{editingMatch.baby_name.name}</span>
                  <GenderIcon gender={editingMatch.baby_name.gender} />
                </div>
                {editingMatch.baby_name.meaning && (
                  <p className="text-white/70 text-sm italic">{editingMatch.baby_name.meaning}</p>
                )}
              </div>

              {/* Comment Input */}
              <div className="p-4">
                <label className="text-xs font-medium text-white/80 mb-2 block">{t('matchesPage.yourComment')}</label>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder={t('matchesPage.commentPlaceholder')}
                  rows={4}
                  className="w-full p-3 rounded-[10px] bg-white/10 border border-white/25 text-white placeholder:text-white/50 text-sm resize-none focus:outline-none focus:border-white/40"
                />
              </div>

              {/* Actions */}
              <div className="px-4 flex gap-3">
                <button
                  onClick={handleCloseNoteModal}
                  className="flex-1 py-3 rounded-[15px] glass-effect border border-white/25 text-white font-medium"
                >
                  {t('matchesPage.cancel')}
                </button>
                <button
                  onClick={handleSaveNote}
                  className="flex-1 py-3 rounded-[15px] bg-pp-orange text-white font-medium"
                >
                  {t('matchesPage.save')}
                </button>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}
